import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.auditkpi.uz/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });
          
          localStorage.setItem('access_token', response.data.access);
          originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
          
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      } else {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// API methods
export const authAPI = {
  login: (data) => api.post('/auth/login/', data),
  logout: (data) => api.post('/auth/logout/', data),
  me: () => api.get('/auth/me/'),
  updateMe: (data) => api.patch('/auth/me/', data),
  changePassword: (data) => api.post('/auth/change-password/', data),
  getUsers: (params) => api.get('/auth/users/', { params }),
  createUser: (data) => api.post('/auth/users/', data),
  updateUser: (id, data) => api.patch(`/auth/users/${id}/`, data),
  deleteUser: (id) => api.delete(`/auth/users/${id}/`),
  getOneIDConfig: () => api.get('/auth/oneid/config/'),
  updateOneIDConfig: (data) => api.put('/auth/oneid/config/', data),
  getOneIDAuthURL: () => api.get('/auth/oneid/auth-url/'),
  linkTelegram: (data) => api.post('/auth/telegram/link/', data),
  unlinkTelegram: () => api.delete('/auth/telegram/link/'),
};

export const organizationsAPI = {
  getAll: (params) => api.get('/organizations/', { params }),
  getOne: (id) => api.get(`/organizations/${id}/`),
  create: (data) => api.post('/organizations/', data),
  update: (id, data) => api.patch(`/organizations/${id}/`, data),
  delete: (id) => api.delete(`/organizations/${id}/`),
  getStatistics: () => api.get('/organizations/statistics/'),
  getRegions: () => api.get('/organizations/regions/'),
  getTypes: () => api.get('/organizations/types/'),
  bulkCreate: (data) => api.post('/organizations/bulk-create/', data),
};

export const tasksAPI = {
  getAll: (params) => api.get('/tasks/', { params }),
  getOne: (id) => api.get(`/tasks/${id}/`),
  create: (data) => api.post('/tasks/', data),
  update: (id, data) => api.patch(`/tasks/${id}/`, data),
  delete: (id) => api.delete(`/tasks/${id}/`),
  getCategories: () => api.get('/tasks/categories/'),
  createCategory: (data) => api.post('/tasks/categories/', data),
  updateCategory: (id, data) => api.patch(`/tasks/categories/${id}/`, data),
  deleteCategory: (id) => api.delete(`/tasks/categories/${id}/`),
  getStatistics: () => api.get('/tasks/statistics/'),
  getBadgeCounts: () => api.get('/tasks/badge-counts/'),
  getMyTasks: () => api.get('/tasks/my-tasks/'),
  viewAssignment: (id) => api.post(`/tasks/assignments/${id}/view/`),
  submitAssignment: (id, data) => api.post(`/tasks/assignments/${id}/submit/`, data),
  reviewAssignment: (id, data) => api.post(`/tasks/assignments/${id}/review/`, data),
  uploadAttachment: (taskId, data) => api.post(`/tasks/${taskId}/attachments/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  addComment: (taskId, data) => api.post(`/tasks/${taskId}/comments/`, data),
};

export const kpiAPI = {
  getCategories: () => api.get('/kpi/categories/'),
  createCategory: (data) => api.post('/kpi/categories/', data),
  updateCategory: (id, data) => api.patch(`/kpi/categories/${id}/`, data),
  deleteCategory: (id) => api.delete(`/kpi/categories/${id}/`),
  getScores: (params) => api.get('/kpi/scores/', { params }),
  createScore: (data) => api.post('/kpi/scores/', data),
  updateScore: (id, data) => api.patch(`/kpi/scores/${id}/`, data),
  getSummaries: (params) => api.get('/kpi/summaries/', { params }),
  getRanking: (params) => api.get('/kpi/ranking/', { params }),
  calculate: (data) => api.post('/kpi/calculate/', data),
  getDashboard: () => api.get('/kpi/dashboard/'),
  getReports: () => api.get('/kpi/reports/'),
};

export const reportsAPI = {
  getAll: (params) => api.get('/reports/', { params }),
  getOne: (id) => api.get(`/reports/${id}/`),
  create: (data) => api.post('/reports/', data),
  update: (id, data) => api.patch(`/reports/${id}/`, data),
  delete: (id) => api.delete(`/reports/${id}/`),
  getStatistics: (params) => api.get('/reports/statistics/', { params }),
};

export const workplansAPI = {
  getAll: (params) => api.get('/workplans/', { params }),
  getOne: (id) => api.get(`/workplans/${id}/`),
  create: (data) => api.post('/workplans/', data),
  update: (id, data) => api.patch(`/workplans/${id}/`, data),
  delete: (id) => api.delete(`/workplans/${id}/`),
  approve: (id) => api.post(`/workplans/${id}/approve/`),
  getStatistics: (params) => api.get('/workplans/statistics/', { params }),
  getOverdueItems: () => api.get('/workplans/overdue-items/'),
  
  // Bandlar
  getItems: (planId) => api.get(`/workplans/${planId}/items/`),
  createItem: (planId, data) => api.post(`/workplans/${planId}/items/`, data),
  updateItem: (planId, itemId, data) => api.patch(`/workplans/${planId}/items/${itemId}/`, data),
  deleteItem: (planId, itemId) => api.delete(`/workplans/${planId}/items/${itemId}/`),
  completeItem: (planId, itemId) => api.post(`/workplans/${planId}/items/${itemId}/complete/`),
  
  // Bajarilgan ishlar
  getExecutions: (itemId) => api.get(`/workplans/items/${itemId}/executions/`),
  createExecution: (itemId, data) => api.post(`/workplans/items/${itemId}/executions/`, data),
  updateExecution: (itemId, executionId, data) => api.patch(`/workplans/items/${itemId}/executions/${executionId}/`, data),
  deleteExecution: (itemId, executionId) => api.delete(`/workplans/items/${itemId}/executions/${executionId}/`),
  
  // Fayllar
  uploadFiles: (executionId, formData) => api.post(`/workplans/executions/${executionId}/files/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteFile: (executionId, fileId) => api.delete(`/workplans/executions/${executionId}/files/`, { data: { file_id: fileId } }),
};

export const documentsAPI = {
  getAll: (params) => api.get('/documents/', { params }),
  getOne: (id) => api.get(`/documents/${id}/`),
  create: (data) => api.post('/documents/', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.patch(`/documents/${id}/`, data),
  delete: (id) => api.delete(`/documents/${id}/`),
};

export const notificationsAPI = {
  getAll: () => api.get('/notifications/'),
  markAsRead: (id) => api.post(`/notifications/${id}/mark-read/`),
  markAllAsRead: () => api.post('/notifications/mark-read/'),
  getUnreadCount: () => api.get('/notifications/unread-count/'),
  
  // Telegram
  telegramGenerateCode: () => api.post('/notifications/telegram/generate-code/'),
  telegramUnlink: () => api.post('/notifications/telegram/unlink/'),
  telegramStatus: () => api.get('/notifications/telegram/status/'),
  telegramToggle: () => api.post('/notifications/telegram/toggle/'),
  telegramTest: () => api.post('/notifications/telegram/test/'),
};

export const chatAPI = {
  getRooms: () => api.get('/chat/rooms/'),
  getRoom: (id) => api.get(`/chat/rooms/${id}/`),
  createRoom: (data) => api.post('/chat/rooms/', data),
  updateRoom: (id, data) => api.patch(`/chat/rooms/${id}/`, data),
  getMessages: (roomId, params) => api.get(`/chat/rooms/${roomId}/messages/`, { params }),
  sendMessage: (roomId, data) => api.post(`/chat/rooms/${roomId}/messages/`, data),
  sendMessageWithFile: (roomId, formData) => api.post(`/chat/rooms/${roomId}/messages/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  markAsRead: (roomId) => api.post(`/chat/rooms/${roomId}/mark-read/`),
  addParticipants: (roomId, userIds) => api.post(`/chat/rooms/${roomId}/add-participants/`, { user_ids: userIds }),
  removeParticipant: (roomId, userId) => api.post(`/chat/rooms/${roomId}/remove-participant/`, { user_id: userId }),
  leaveRoom: (roomId) => api.post(`/chat/rooms/${roomId}/leave/`),
  startPrivateChat: (data) => api.post('/chat/start-private/', data),
  getUsers: () => api.get('/auth/users/'),
};

export const monitoringAPI = {
  getDashboard: () => api.get('/monitoring/dashboard/'),
};

export const risksAPI = {
  getAll: (params) => api.get('/risks/', { params }),
  getOne: (id) => api.get(`/risks/${id}/`),
  create: (data) => api.post('/risks/', data),
  update: (id, data) => api.patch(`/risks/${id}/`, data),
  delete: (id) => api.delete(`/risks/${id}/`),
};

export const archiveAPI = {
  getTasks: (params) => api.get('/archive/tasks/', { params }),
  getReports: (params) => api.get('/archive/reports/', { params }),
};

export default api;
